import { createAuthProvider } from '@avantia/azure-ad-auth';
import { addToWindowRef, tokenPrefix, tokenSuffix, voidFunction } from '@avantia/client-and-server-utilities';
import { registerBespokeExpressionParser } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { HpControlActionTypes, convertTokensInTemplate } from '@avantia/questionset-model';
import { registerCustomSvgIcons } from '@avantia/style-guide';
import { MsalProvider } from '@azure/msal-react';
import { ReactComponent as AngleDoubleLeftIcon } from '@fortawesome/fontawesome-free/svgs/solid/angle-double-left.svg';
import { ReactComponent as AngleDoubleRightIcon } from '@fortawesome/fontawesome-free/svgs/solid/angle-double-right.svg';
import { ReactComponent as AngleDownIcon } from '@fortawesome/fontawesome-free/svgs/solid/angle-down.svg';
import { ReactComponent as AngleLeftIcon } from '@fortawesome/fontawesome-free/svgs/solid/angle-left.svg';
import { ReactComponent as AngleRightIcon } from '@fortawesome/fontawesome-free/svgs/solid/angle-right.svg';
import { ReactComponent as CheckCircle } from '@fortawesome/fontawesome-free/svgs/solid/check-circle.svg';
import { ReactComponent as CheckIcon } from '@fortawesome/fontawesome-free/svgs/solid/check.svg';
import { ReactComponent as ExclamationCircle } from '@fortawesome/fontawesome-free/svgs/solid/exclamation-circle.svg';
import { ReactComponent as FileInvoiceIcon } from '@fortawesome/fontawesome-free/svgs/solid/file-invoice.svg';
import { ReactComponent as InfoIcon } from '@fortawesome/fontawesome-free/svgs/solid/info-circle.svg';
import { ReactComponent as PlusIcon } from '@fortawesome/fontawesome-free/svgs/solid/plus.svg';
import { ReactComponent as CrossCircleWhiteIcon } from '@fortawesome/fontawesome-free/svgs/solid/times-circle.svg';
import { ReactComponent as CrossIcon } from '@fortawesome/fontawesome-free/svgs/solid/times.svg';
import jQuery from 'jquery';
import 'react-app-polyfill/ie11';
import * as ReactDOMClient from 'react-dom/client';
import { Provider } from 'react-redux';
import { setJourneyPhaseButtonClickAction } from './actions/setJourneyPhaseAction';
import { submitClaimAction } from './actions/submitClaimAction';
import { toggleModalDependentAction } from './actions/toggleModalAction';
import { transferToCustomerPortalDependentAction } from './actions/transferToCustomerPortalAction';
import { validateBankDetailsAction } from './actions/validateBankDetailsAction';
import './assets/style/site.scss';
import { ReactComponent as AlertComment } from './assets/svg/alert-comment.svg';
import { ReactComponent as WarningIcon } from './assets/svg/warning-icon.svg';
import { HpClaimsDebugFlags, logInfo } from './clientLogging';
import App from './components/app';
import { getClaimsJourneyNavigationTabControl } from './components/formControls/common/getClaimsJourneyNavigationTabControl';
import * as customControls from './components/formControls/custom/index';
import { ClaimsJourneyHeadingControl, ClaimsJourneyInnerHeadingControl } from './components/headings';
import { getConfig } from './config/configuration';
import { configureThirdPartyFeatures } from './config/configureThirdPartyFeatures';
import * as customFunctions from './customFunctions';
import { getAddressItemAction } from './engineClient/actions/getAddressItemAction';
import { searchForAddresses } from './engineClient/actions/getAddressLookupAction';
import { addBasicDropDownKeyboardNavigationHandler } from './engineClient/components/formControls/common/basicDropDownListControl';
import { registerNavigationTabControls } from './engineClient/components/formControls/common/getNavigationTabControl';
import { addAutoCompleteKeyboardNavigationHandler } from './engineClient/components/formControls/standard';
import { registerHeadingControl } from './engineClient/components/headings/getHeadingControl';
import { registerCustomControls } from './engineClient/components/questionSetForm/questionSetPageFunctions';
import { registerClientConfig } from './engineClient/config/registerClientConfig';
import { configureFeatures } from './engineClient/configureFeatures';
import { registerCustomExtension } from './engineClient/customExtensionRegistry';
import { registerCustomFunctions } from './engineClient/customFunctionRegistry';
import { registerAllHashKeys } from './engineClient/handlers/handlerUtilities';
import { registerCustomHashAndAnswersHandler } from './engineClient/handlers/pageLoadDataHandler';
import { registerPerQuestionSetConfig } from './engineClient/perQuestionSetConfigRegistry';
import {
  addressLookupButtonClickAction,
  registerAddressLookupFeature
} from './engineClient/reducers/addressLookupFeatureReducer';
import { registerIsValidControlTypeFunction } from './engineClient/reducers/questionSetReducerLibrary';
import { addTopLevelEventListener } from './engineClient/tools/addTopLevelEventListener';
import { runIeHacks } from './engineClient/tools/ie-hacks';
import { registerQuestionIds } from './engineClient/tools/questionIds';
import { clearAndLoadingHandler } from './handlers/clearAndLoadingHandler';
import { getAgentCookie, isAzureAdSession, setAgentCookie, setOriginSite } from './handlers/handlerUtilities';
import { allHashKeys } from './handlers/handlerTypes';
import { isValidClaimsControlType } from './reducers/isValidClaimsControlType';
import {
  sortQuestionIdNodesUsingListExtension,
  validateAndMaybeMoveSectionExtension
} from './reducers/reducerExtensions';
import reportWebVitals from './reportWebVitals';
import { configureStore } from './store/configureStore';
import { productId, questionIds, sectionIds } from './types/questionConstants';

const config = getConfig();

registerAllHashKeys(allHashKeys);
registerQuestionIds({ [productId]: { questionIds, sectionIds } });
registerClientConfig(config);
registerHeadingControl({ ClaimsJourneyHeadingControl, ClaimsJourneyInnerHeadingControl });
registerNavigationTabControls({ getClaimsJourneyNavigationTabControl });
registerCustomControls({ type: 'standard', controls: customControls as any });
registerCustomFunctions('*', customFunctions);
registerPerQuestionSetConfig({
  productId: '35d65b5d-e0ac-4f68-8251-10d77658e397',
  cleanAnswersOnLoad: false,
  modifyAnswers: voidFunction
});
registerCustomExtension('validateAndMaybeMoveSection', validateAndMaybeMoveSectionExtension);
registerCustomExtension('sortQuestionIdNodesUsingList', sortQuestionIdNodesUsingListExtension);

// Submodules can't import svg components, so we need to do so here.
registerCustomSvgIcons({
  'alert-comment': AlertComment,
  'angle-left': AngleLeftIcon,
  'angle-right': AngleRightIcon,
  'angle-down': AngleDownIcon,
  'angle-double-left': AngleDoubleLeftIcon,
  'angle-double-right': AngleDoubleRightIcon,
  check: CheckIcon,
  'check-circle': CheckCircle,
  cross: CrossIcon,
  'cross-circle-white': CrossCircleWhiteIcon,
  'exclamation-circle': ExclamationCircle,
  'file-invoice': FileInvoiceIcon,
  info: InfoIcon,

  plus: PlusIcon,
  warning: WarningIcon,
  'warning-amber': WarningIcon
});

registerBespokeExpressionParser((block: string) => {
  if (block.indexOf(tokenPrefix) >= 0 && block.indexOf(tokenSuffix) > 0) {
    const code = convertTokensInTemplate(block);
    logInfo(`Validator expression: [${block}] => [${code}]`, HpClaimsDebugFlags.All);
    return `return ${code};`;
  }

  return block;
});

registerCustomHashAndAnswersHandler(clearAndLoadingHandler);

registerIsValidControlTypeFunction(isValidClaimsControlType);

configureThirdPartyFeatures(config);

configureFeatures([
  {
    key: 'address-lookup',
    createActionPromise: searchForAddresses,
    buttonClickAction: addressLookupButtonClickAction,
    registerFeature: registerAddressLookupFeature
  },
  {
    key: 'address-select',
    createActionPromise: getAddressItemAction,
    buttonClickAction: undefined,
    registerFeature: undefined
  },
  {
    key: 'submit-claim' as HpControlActionTypes,
    createActionPromise: submitClaimAction,
    buttonClickAction: undefined,
    registerFeature: undefined
  },
  {
    key: 'validate-bank-details' as HpControlActionTypes,
    createActionPromise: validateBankDetailsAction,
    buttonClickAction: undefined,
    registerFeature: undefined
  },
  {
    key: 'exit' as HpControlActionTypes,
    createActionPromise: transferToCustomerPortalDependentAction,
    buttonClickAction: undefined,
    registerFeature: undefined
  },
  {
    key: 'toggle-modal' as HpControlActionTypes,
    createActionPromise: toggleModalDependentAction,
    buttonClickAction: undefined,
    registerFeature: undefined
  },
  {
    key: 'set-journey-phase' as HpControlActionTypes,
    createActionPromise: () => undefined,
    buttonClickAction: setJourneyPhaseButtonClickAction,
    registerFeature: undefined
  }
]);

const store = configureStore();
const container = document.getElementById('root') as HTMLElement;
const root = ReactDOMClient.createRoot(container);
const { azureAdClientId, azureAdTenantId, websiteBaseUrl: baseUrl, environment: env } = config;
const isDeveloperEnv = env === 'developer' || env === 'unit-tests';
const websiteBaseUrl = isDeveloperEnv ? 'http://localhost:3000' : baseUrl;
const authProvider = createAuthProvider({
  clientId: azureAdClientId,
  tenantId: azureAdTenantId,
  websiteBaseUrl
});
let useAd = !!getAgentCookie();
let isOriginSet = false;
const { search } = window.location;
const params = new URLSearchParams(search);
if (!useAd) {
  const emailAddress = params.get('emailAddress');
  if (emailAddress) {
    setAgentCookie(emailAddress);
    useAd = true;
  }
}

if (!useAd && isAzureAdSession()) {
  useAd = true;
}

if (!isOriginSet) {
  const origin = params.get('origin');

  if (origin) {
    setOriginSite(origin);
    isOriginSet = true;
  }
}

const appElement = (
  <Provider store={store}>
    <App />
  </Provider>
);

if (useAd) {
  root.render(<MsalProvider instance={authProvider}>{appElement}</MsalProvider>);
} else {
  root.render(appElement);
}

// GTM needs jQuery and it helps when debugging.
addToWindowRef({ $: jQuery, jQuery, lodash }, (win: Window) => {
  addAutoCompleteKeyboardNavigationHandler(win, jQuery);
  addBasicDropDownKeyboardNavigationHandler(win, jQuery);
  addTopLevelEventListener();
  runIeHacks();
  return true; // add the provided object to window.
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
